import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { createSocket } from "@/lib/socket";
import { useAuth } from "@/context/AuthContext";
import CountdownTimer from "@/components/CountdownTimer";
import ChatRoom from "@/components/ChatRoom";
import { toast } from "sonner";
import {
  CheckCircle, Heart, Trophy, Storefront, Lightning, ArrowsLeftRight, Question,
  MapPin, Truck, Sparkle, ShoppingBag, ShieldCheck, Star, ClockCounterClockwise, Flag,
  ChatCircleText, CaretRight, CaretLeft, Package, Tag, Calendar, Info, ChatCircle,
} from "@phosphor-icons/react";
import SharePopover from "@/components/SharePopover";
import AuctionCard from "@/components/AuctionCard";

const fallback = "https://images.unsplash.com/photo-1611095973362-88e8e2557e58?w=1200&q=80";
const imgSrc = (p) => (!p ? fallback : p.startsWith("http") ? p : fileUrl(p));

export default function AuctionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [auction, setAuction] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerListingsCount, setSellerListingsCount] = useState(0);
  const [similar, setSimilar] = useState([]);
  const [bids, setBids] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [flash, setFlash] = useState(false);
  const [fav, setFav] = useState(false);
  const [qText, setQText] = useState("");
  const [answers, setAnswers] = useState({});
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showDopingModal, setShowDopingModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [viewers, setViewers] = useState(0);

  const load = useCallback(async () => {
    try {
      const [a, b, qq] = await Promise.all([
        api.get(`/auctions/${id}`),
        api.get(`/auctions/${id}/bids`),
        api.get(`/auctions/${id}/questions`),
      ]);
      setAuction(a.data); setBids(b.data); setQuestions(qq.data);
      if (a.data.listing_type === "auction") {
        const min = a.data.bid_count > 0 ? a.data.current_price + a.data.min_increment : a.data.current_price;
        setBidAmount(String(min));
      }
      // Load seller (with listings count) for verified badge etc.
      try {
        const sr = await api.get(`/users/${a.data.seller_id}`);
        setSeller(sr.data.user);
        setSellerListingsCount(
          (sr.data.listings || []).filter((l) => l.status === "active").length
        );
      } catch (e) { /* ignore */ }

      // Similar listings — same category, active, excluding current
      try {
        const sim = await api.get(`/auctions?category=${a.data.category}&status=active&limit=8`);
        const items = sim.data.items || sim.data || [];
        setSimilar(items.filter((x) => x.id !== a.data.id).slice(0, 4));
      } catch (e) { /* ignore */ }
    } catch {
      toast.error("İlan yüklenemedi");
      nav("/kesfet");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // SEO title
  useEffect(() => {
    if (auction?.title) document.title = `${auction.title} — kolaysatis.com`;
    return () => { document.title = "kolaysatis.com"; };
  }, [auction?.title]);

  // JSON-LD Product schema for SEO
  const jsonLd = auction ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": auction.title,
    "description": auction.description?.slice(0, 500),
    "image": auction.images?.[0],
    "category": auction.category,
    "offers": {
      "@type": "Offer",
      "price": auction.current_price,
      "priceCurrency": "TRY",
      "availability": auction.status === "active" ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      "url": typeof window !== "undefined" ? window.location.href : "",
    },
  } : null;

  useEffect(() => {
    if (!user) { setFav(false); return; }
    api.get("/me/favorites").then(({ data }) => setFav(data.some((a) => a.id === id))).catch(() => {});
  }, [user, id]);

  useEffect(() => {
    if (!auction) return;
    const s = createSocket();
    socketRef.current = s;
    s.on("connect", () => {
      s.emit("join_auction", { auction_id: id });
      if (user) s.emit("join_user", { user_id: user.user_id });
    });
    s.on("new_bid", (data) => {
      if (data.auction_id !== id) return;
      setAuction((prev) => prev ? {
        ...prev, current_price: data.current_price, bid_count: data.bid_count,
        highest_bidder_id: data.bid.user_id, highest_bidder_name: data.highest_bidder_name,
      } : prev);
      setBids((prev) => [data.bid, ...prev]);
      setBidAmount(String(data.current_price + (auction.min_increment || 10)));
      setFlash(true); setTimeout(() => setFlash(false), 1200);
    });
    s.on("auction_closed", (data) => {
      if (data.auction_id !== id) return;
      setAuction((prev) => prev ? { ...prev, status: "closed", winner_id: data.winner_id } : prev);
      toast(data.winner_name ? `İlan bitti — Kazanan: ${data.winner_name}` : "İlan sona erdi");
      load();
    });
    s.on("auction_extended", (data) => {
      if (data.auction_id !== id) return;
      setAuction((prev) => prev ? {
        ...prev,
        end_time: data.new_end_time,
        extension_count: data.extension_count,
        is_hot_auction: data.is_hot_auction ?? prev.is_hot_auction,
      } : prev);
      toast("⏱️ Anti-sniper: süre 60 sn uzatıldı!");
    });
    s.on("viewers_count", (data) => {
      if (data.auction_id === id) setViewers(data.count);
    });
    return () => { if (s.connected) s.disconnect(); };
    // eslint-disable-next-line
  }, [auction?.id, id, auction?.min_increment, user?.user_id]);

  const placeBid = async () => {
    if (!user) { toast.error("Teklif için giriş yapın"); nav("/giris"); return; }
    if (!user.is_verified) { toast.error("Teklif için kimlik doğrulaması gerekli"); nav("/dogrula"); return; }
    const amt = Number(bidAmount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Geçerli bir teklif tutarı girin"); return; }
    if (auction?.buy_now_price && amt > auction.buy_now_price) {
      toast.error("Teklifiniz Hemen Al fiyatından yüksek olamaz.");
      return;
    }
    setPlacing(true);
    try {
      await api.post(`/auctions/${id}/bids`, { amount: amt });
      toast.success("Teklif verildi!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Teklif reddedildi");
    } finally { setPlacing(false); }
  };

  const buyNow = async () => {
    if (!user) return nav("/giris");
    if (!user.is_verified) { toast.error("Alım için kimlik doğrulaması gerekli"); return nav("/dogrula"); }
    try {
      const { data } = await api.post(`/auctions/${id}/buy-now`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başlatılamadı");
    }
  };

  const toggleFav = async () => {
    if (!user) return nav("/giris");
    try {
      if (fav) { await api.delete(`/favorites/${id}`); setFav(false); }
      else { await api.post(`/favorites/${id}`); setFav(true); }
    } catch { /* ignore */ }
  };

  const startCheckout = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { auction_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ödeme başlatılamadı");
    }
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!user) return nav("/giris");
    try {
      await api.post(`/auctions/${id}/questions`, { text: qText });
      setQText(""); toast.success("Sorunuz gönderildi"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const submitAnswer = async (qid) => {
    const text = answers[qid];
    if (!text?.trim()) return;
    try {
      await api.post(`/questions/${qid}/answer`, { text });
      setAnswers({ ...answers, [qid]: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const confirmDelivery = async () => {
    try {
      await api.post(`/auctions/${id}/release-escrow`);
      toast.success("Teslim alındı olarak işaretlendi"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  if (!auction) return <div className="max-w-[1400px] mx-auto px-6 py-16 text-slate-500">Yükleniyor…</div>;

  const isSeller = user?.user_id === auction.seller_id;
  const isWinner = user?.user_id === auction.winner_id;
  const closed = auction.status !== "active";
  const openEnded = auction.listing_type === "open_ended";
  const minRequired = auction.bid_count > 0 ? auction.current_price + auction.min_increment : auction.current_price;
  const urgent = auction.doping_type === "urgent_badge";

  return (
    <div data-testid="auction-detail-page">
      {/* JSON-LD structured data for SEO */}
      {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />}

      {/* Glowing urgent banner — matte terracotta */}
      {urgent && (
        <div className="urgent-shine text-white text-[11px] uppercase tracking-[0.32em] font-medium py-2.5 text-center flex items-center justify-center gap-3">
          <Lightning size={14} weight="fill" />
          Acil satılık
          <Lightning size={14} weight="fill" />
        </div>
      )}

      <div className="max-w-[1280px] mx-auto px-6 py-14">
        <div className="mb-10 flex items-center gap-3 text-sm flex-wrap">
          <Link to="/kesfet" className="text-slate-400 hover:text-slate-900 transition-colors">Keşfet</Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-500 text-xs tracking-[0.2em] uppercase font-medium">{auction.category}</span>
          {auction.city && (
            <>
              <span className="text-slate-300">/</span>
              <span className="flex items-center gap-1 text-slate-400 text-xs">
                <MapPin size={12} /> {auction.city}{auction.district ? ` · ${auction.district}` : ""}
              </span>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 xl:gap-20">
          {/* ============ LEFT: Editorial article-flow ============ */}
          <div className="lg:col-span-8 xl:col-span-8">
            {/* Gallery — larger main image with counter + prev/next arrows */}
            <div className="rounded-2xl overflow-hidden bg-slate-50">
              <div className="relative aspect-[4/3] bg-slate-50 overflow-hidden group">
                <button
                  type="button"
                  onClick={() => {
                    const total = auction.images?.length || 0;
                    if (total > 1) setActiveImage((activeImage + 1) % total);
                  }}
                  className="block w-full h-full cursor-pointer"
                  aria-label="Sonraki görsel"
                  data-testid="gallery-main-image"
                >
                  <img
                    src={imgSrc(auction.images?.[activeImage])}
                    alt={auction.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    onError={(e) => { e.target.src = fallback; }}
                  />
                </button>

                {/* Image counter overlay */}
                {auction.images?.length > 1 && (
                  <div
                    data-testid="gallery-counter"
                    className="absolute bottom-4 right-4 bg-slate-900/80 backdrop-blur-sm text-white text-xs font-mono-tight px-3 py-1.5 rounded-full pointer-events-none"
                  >
                    {activeImage + 1} / {auction.images.length}
                  </div>
                )}

                {/* Prev/Next arrow buttons on desktop */}
                {auction.images?.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const total = auction.images.length;
                        setActiveImage((activeImage - 1 + total) % total);
                      }}
                      className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all opacity-0 group-hover:opacity-100 premium-border shadow-sm"
                      aria-label="Önceki görsel"
                      data-testid="gallery-prev"
                    >
                      <CaretLeft size={16} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const total = auction.images.length;
                        setActiveImage((activeImage + 1) % total);
                      }}
                      className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all opacity-0 group-hover:opacity-100 premium-border shadow-sm"
                      aria-label="Sonraki görsel"
                      data-testid="gallery-next"
                    >
                      <CaretRight size={16} weight="bold" />
                    </button>
                  </>
                )}
              </div>

              {auction.images?.length > 1 && (
                <div className="grid grid-cols-5 gap-2.5 p-3" data-testid="gallery-thumbs">
                  {auction.images.slice(0, 10).map((im, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`aspect-square rounded-lg overflow-hidden transition-all ${activeImage === i ? "ring-2 ring-slate-900 ring-offset-2" : "opacity-70 hover:opacity-100 hover:ring-1 hover:ring-slate-300"}`}
                    >
                      <img src={imgSrc(im)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title */}
            <div className="pt-14 pb-8">
              <h1 className={`font-display tracking-tight leading-[1.08] text-slate-900 ${auction.doping_type === "bold_title" ? "text-4xl md:text-5xl font-medium text-[#B54037]" : "text-3xl md:text-4xl lg:text-5xl font-medium"}`}>
                {auction.title}
              </h1>
              <div className="flex flex-wrap gap-2 mt-6">
                <Badge icon={Sparkle} label={auction.condition === "new" ? "Sıfır" : "İkinci El"} tint={auction.condition === "new" ? "emerald" : "slate"} />
                <Badge icon={Truck} label={({ kargo: "Kargo (alıcı öder)", dahil: "Kargo dahil", elden: "Elden teslim" })[auction.shipping_type] || auction.shipping_type} tint="slate" />
                {auction.city && <Badge icon={MapPin} label={auction.city} tint="slate" />}
              </div>
            </div>

            {/* Description — as card */}
            <div className="rounded-2xl bg-white premium-border p-6 md:p-8 mb-6" data-testid="description-card">
              <div className="flex items-center gap-2 mb-5">
                <Info size={16} weight="regular" className="text-slate-500" />
                <h2 className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500">Açıklama</h2>
              </div>
              <p className="text-base leading-[1.75] whitespace-pre-line text-slate-700">{auction.description}</p>
            </div>

            {/* Product info grid — cards/boxes */}
            <div className="rounded-2xl bg-white premium-border p-6 md:p-8 mb-6" data-testid="product-info-card">
              <div className="flex items-center gap-2 mb-6">
                <Package size={16} weight="regular" className="text-slate-500" />
                <h2 className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-500">Ürün bilgileri</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                <InfoRow icon={Tag} label="Kategori" value={auction.category} />
                {auction.subcategory && <InfoRow icon={Tag} label="Alt kategori" value={auction.subcategory} />}
                <InfoRow icon={Sparkle} label="Durum" value={auction.condition === "new" ? "Sıfır" : "İkinci El"} />
                <InfoRow icon={Truck} label="Teslimat" value={({ kargo: "Kargo (alıcı öder)", dahil: "Kargo dahil", elden: "Elden teslim" })[auction.shipping_type] || auction.shipping_type} />
                {auction.city && <InfoRow icon={MapPin} label="Şehir" value={`${auction.city}${auction.district ? ` · ${auction.district}` : ""}`} />}
                <InfoRow icon={Calendar} label="İlan tarihi" value={new Date(auction.created_at).toLocaleDateString("tr-TR")} />
                {!openEnded && auction.end_time && (
                  <InfoRow icon={ClockCounterClockwise} label="Bitiş" value={new Date(auction.end_time).toLocaleDateString("tr-TR")} />
                )}
                {auction.listing_type && (
                  <InfoRow icon={Info} label="İlan tipi" value={openEnded ? "Süresiz (Hemen Al)" : "Açık artırma"} />
                )}
              </div>
            </div>

            <div className="border-b divider mb-2" />

            {/* BID HISTORY — flat table with divider */}
            {!openEnded && (
              <div className="pt-14 pb-12 border-b divider" data-testid="bid-history">
                <div className="flex items-baseline justify-between mb-8">
                  <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900">Teklif geçmişi</h2>
                  <span className="text-xs uppercase tracking-[0.24em] font-medium text-slate-400">{bids.length} teklif</span>
                </div>
                {bids.length === 0 ? (
                  <p className="text-slate-400 text-sm">Henüz teklif yok — ilk teklifi sen ver.</p>
                ) : (
                  <div className="max-w-2xl">
                    {bids.map((b, i) => (
                      <div key={b.id} className={`flex items-center justify-between py-4 border-b divider last:border-0 ${i === 0 ? "bid-flash" : ""}`}>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{b.user_name}</p>
                          {i === 0 && <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-emerald-700 mt-0.5">En yüksek</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-mono-tight text-base font-medium text-slate-900">₺{Number(b.amount).toLocaleString("tr-TR")}</p>
                          <p className="font-mono-tight text-[11px] text-slate-400 mt-0.5">{new Date(b.timestamp).toLocaleString("tr-TR")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Q&A — flat editorial */}
            <div className="pt-14 pb-4" data-testid="qa-section">
              <div className="flex items-baseline justify-between mb-8">
                <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 flex items-center gap-2">
                  <Question size={20} weight="regular" /> Satıcıya sorular
                </h2>
                <span className="text-xs uppercase tracking-[0.24em] font-medium text-slate-400">{questions.length} soru</span>
              </div>

              {!isSeller && user && (
                <form onSubmit={submitQuestion} className="mb-10 max-w-2xl">
                  <textarea
                    rows={2} value={qText}
                    onChange={(e) => setQText(e.target.value)}
                    placeholder="Satıcıya bir soru sor (telefon/e-posta yasak)…"
                    className="w-full bg-white border border-slate-200 rounded-lg p-3.5 focus:outline-none focus:border-slate-900 text-sm text-slate-800 transition-colors"
                    data-testid="q-input" required minLength={3}
                  />
                  <button
                    type="submit"
                    className="mt-3 bg-slate-900 text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors"
                    data-testid="q-submit"
                  >
                    Gönder
                  </button>
                </form>
              )}

              <div className="max-w-2xl">
                {questions.length === 0 ? (
                  <p className="text-slate-400 text-sm">Henüz soru yok.</p>
                ) : questions.map((q) => (
                  <div key={q.id} className="py-6 border-b divider last:border-0">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <p className="text-sm font-medium text-slate-900">{q.asker_name}</p>
                      <p className="text-[11px] text-slate-400 font-mono-tight">{new Date(q.created_at).toLocaleString("tr-TR")}</p>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{q.text}</p>
                    {q.answer ? (
                      <div className="mt-4 pl-4 border-l-2 border-slate-200">
                        <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-1">Satıcı yanıtı</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{q.answer}</p>
                      </div>
                    ) : isSeller ? (
                      <div className="mt-3 flex gap-2">
                        <input
                          value={answers[q.id] || ""}
                          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                          placeholder="Cevap yaz…"
                          className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-900 transition-colors"
                          data-testid={`answer-input-${q.id}`}
                        />
                        <button
                          onClick={() => submitAnswer(q.id)}
                          className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                          data-testid={`answer-submit-${q.id}`}
                        >Cevapla</button>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 mt-2 italic">Satıcı henüz cevaplamadı</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ============ RIGHT: Floating widget (narrow, glassy) ============ */}
          <div className="lg:col-span-4 xl:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-3">
              {/* Live status + fav + share */}
              <div className="flex items-center justify-between pb-1">
                <p className="text-[10px] uppercase tracking-[0.28em] font-medium flex items-center gap-2">
                  {closed ? (
                    <span className="text-slate-400 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Sona erdi</span>
                  ) : openEnded ? (
                    <span className="text-slate-500">Süresiz</span>
                  ) : (
                    <span className="text-[#B54037] flex items-center gap-2"><span className="live-dot" /> Canlı yayında</span>
                  )}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={toggleFav}
                    className="p-2 rounded-full hover:bg-slate-50 transition-colors"
                    data-testid="fav-toggle"
                  >
                    <Heart size={16} weight={fav ? "fill" : "regular"} color={fav ? "#B54037" : "#475569"} />
                  </button>
                  <SharePopover auction={auction} />
                  {user && !isSeller && (
                    <button
                      onClick={() => setShowReportModal(true)}
                      className="p-2 rounded-full hover:bg-slate-50 transition-colors"
                      aria-label="Şikayet et"
                      data-testid="report-listing-btn"
                    >
                      <Flag size={16} className="text-slate-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Floating widget — glassy panel that holds price, timer, actions */}
              <div className="floating-widget rounded-2xl p-6 space-y-5 premium-border" data-testid="price-action-panel">
                {/* Price */}
                {!openEnded && (
                  <div data-testid="current-highest-bid" className={flash ? "bid-flash rounded-lg p-2 -m-2" : ""}>
                    <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400">Güncel teklif</p>
                    <p className="font-mono-tight text-[42px] font-medium text-slate-900 tracking-tight leading-none mt-2">
                      ₺{Number(auction.current_price).toLocaleString("tr-TR")}
                    </p>
                    <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                      <span>{auction.bid_count} teklif</span>
                      {auction.highest_bidder_name && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="text-slate-700 truncate">{auction.highest_bidder_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Timer — inside widget */}
                {!openEnded && (
                  <div className="flex items-center justify-between pt-5 border-t divider">
                    <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 flex items-center gap-2">
                      {closed ? "Süre" : "Kalan"}
                    </p>
                    <CountdownTimer endTime={auction.end_time} size="lg" onExpire={load} />
                  </div>
                )}

                {/* Bid input */}
                {!closed && !isSeller && !openEnded && (
                  <div className="space-y-3 pt-5 border-t divider">
                    <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 block">
                      Teklifiniz · min ₺{Number(minRequired).toLocaleString("tr-TR")}
                      {auction.buy_now_price ? (
                        <span className="ml-2 text-slate-400 normal-case tracking-normal">
                          · max ₺{Number(auction.buy_now_price).toLocaleString("tr-TR")}
                        </span>
                      ) : null}
                    </label>
                    <input
                      data-testid="bid-input-field"
                      type="number" step="0.01" min={minRequired}
                      max={auction.buy_now_price || undefined}
                      value={bidAmount} onChange={(e) => setBidAmount(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 font-mono-tight text-lg text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                    />
                    {auction.buy_now_price && Number(bidAmount) > auction.buy_now_price && (
                      <p data-testid="bid-limit-warning" className="text-xs text-red-600">
                        Teklifiniz Hemen Al fiyatından yüksek olamaz.
                      </p>
                    )}
                    <button
                      data-testid="place-bid-button"
                      onClick={placeBid}
                      disabled={placing || (auction.buy_now_price && Number(bidAmount) > auction.buy_now_price)}
                      className={
                        auction.buy_now_price
                          ? "w-full bg-white text-slate-900 border-2 border-slate-900 font-medium px-6 py-3 rounded-xl hover:bg-slate-900 hover:text-white transition-all flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-900"
                          : "w-full bg-slate-900 text-white font-medium px-6 py-3.5 rounded-xl hover:bg-black transition-all flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed"
                      }
                    >
                      <span>Teklif ver</span>
                      <span className="font-mono-tight">₺{Number(bidAmount || 0).toLocaleString("tr-TR")}</span>
                    </button>
                  </div>
                )}

                {/* Buy Now — PRIMARY CTA (main conversion driver) */}
                {auction.buy_now_price && !closed && !isSeller && (
                  <button
                    onClick={buyNow}
                    data-testid="buy-now-btn"
                    className="w-full bg-slate-900 text-white font-medium px-6 py-3.5 rounded-xl hover:bg-black transition-all flex items-center justify-between group"
                  >
                    <span className="flex items-center gap-2">
                      <ShoppingBag size={16} weight="fill" /> Hemen al
                    </span>
                    <span className="font-mono-tight text-base">
                      ₺{Number(auction.buy_now_price).toLocaleString("tr-TR")}
                    </span>
                  </button>
                )}

                {/* Trust micro-copy */}
                {!closed && !isSeller && (
                  <div className="pt-4 border-t divider space-y-2" data-testid="trust-microcopy">
                    <p className="text-[11px] text-slate-500 flex items-center gap-2">
                      <ShieldCheck size={12} weight="fill" className="text-emerald-700 shrink-0" />
                      Güvenli işlem · Havuz hesap ile satıcıya ödeme
                    </p>
                    <p className="text-[11px] text-slate-500 flex items-center gap-2">
                      <Sparkle size={12} weight="fill" className="text-slate-500 shrink-0" />
                      AI onaylı ilan · Yasaklı içerik taranmış
                    </p>
                    <p className="text-[11px] text-[#B54037] flex items-center gap-2">
                      <Info size={12} weight="regular" className="shrink-0" />
                      Site dışı ödeme yapmayın — dolandırıcılığa karşı korunun.
                    </p>
                  </div>
                )}
              </div>

              {/* FOMO — live viewers (compact) */}
              {!closed && viewers > 0 && (
                <div
                  data-testid="live-viewers"
                  className="px-4 py-3 flex items-center gap-2 text-sm text-slate-500"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>
                    Şu an <span className="font-mono-tight text-slate-900">{viewers}</span> kişi inceliyor
                  </span>
                </div>
              )}

              {/* Extension tracker */}
              {!openEnded && !closed && (auction.extension_count || 0) > 0 && (
                <div
                  data-testid="extension-tracker"
                  className="px-4 py-3 flex items-start gap-2 text-sm text-[#B54037]"
                >
                  <span className="mt-0.5">↻</span>
                  <p className="leading-tight">
                    Son dakikada <span className="font-mono-tight font-medium">{auction.extension_count}</span> kez uzatıldı — rekabet kızışıyor.
                  </p>
                </div>
              )}

              {/* Offer / Trade — outline */}
              {!closed && !isSeller && auction.accepts_offers && (
                <button
                  onClick={() => setShowOfferModal(true)}
                  data-testid="open-offer-modal"
                  className="w-full bg-white text-slate-900 border border-slate-200 hover:border-slate-900 font-medium px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowsLeftRight size={15} weight="regular" /> Takas / Teklif gönder
                </button>
              )}

              {/* Message seller — direct 1:1 chat */}
              {!isSeller && user && (
                <button
                  onClick={() => setShowMessageModal(true)}
                  data-testid="message-seller-btn"
                  className="w-full bg-white text-slate-900 border border-slate-200 hover:border-slate-900 font-medium px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ChatCircleText size={15} weight="regular" /> Satıcıya mesaj gönder
                </button>
              )}

              {/* Winner CTA */}
              {closed && isWinner && !auction.paid && (
                <div className="p-5 rounded-xl bg-emerald-50 space-y-3">
                  <p className="text-sm font-medium text-emerald-900 flex items-center gap-2">
                    <Trophy size={16} weight="fill" /> Bu ilanı kazandınız.
                  </p>
                  <button
                    data-testid="pay-now-btn"
                    onClick={startCheckout}
                    className="w-full bg-emerald-800 text-white font-medium px-6 py-3 rounded-lg hover:bg-emerald-900 transition-colors"
                  >
                    Ödemeyi tamamla — ₺{Number(auction.current_price).toLocaleString("tr-TR")}
                  </button>
                </div>
              )}

              {closed && auction.paid && (
                <EscrowPanel auction={auction} isWinner={isWinner} onConfirm={confirmDelivery} />
              )}

              {/* Seller doping */}
              {isSeller && !closed && (
                <button
                  onClick={() => setShowDopingModal(true)}
                  data-testid="open-doping"
                  className="w-full bg-white text-slate-900 border border-slate-200 hover:border-slate-900 font-medium px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Lightning size={15} weight="regular" /> İlanına doping uygula
                </button>
              )}

              {/* Seller card */}
              <SellerCard seller={seller} auction={auction} activeListingsCount={sellerListingsCount} />
            </div>
          </div>
        </div>

        {showOfferModal && <OfferModal auctionId={id} listingTitle={auction.title} onClose={() => setShowOfferModal(false)} />}
        {showDopingModal && <DopingModal auctionId={id} onClose={() => setShowDopingModal(false)} />}
        {showReportModal && <ReportModal auctionId={id} onClose={() => setShowReportModal(false)} />}
        {showMessageModal && <MessageSellerModal auctionId={id} sellerName={auction.seller_name} onClose={() => setShowMessageModal(false)} />}

        {/* Similar listings — Benzer ilanlar */}
        {similar.length > 0 && (
          <section className="mt-24 pt-16 border-t divider" data-testid="similar-listings">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Benzer ilanlar</p>
                <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900">
                  Bu kategoriden diğerleri
                </h2>
              </div>
              <Link to={`/kesfet/${auction.category}`} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors hidden md:inline">
                Tümü →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similar.map((sa) => (
                <AuctionCard key={sa.id} auction={sa} />
              ))}
            </div>
          </section>
        )}

        <ChatRoom auctionId={id} sellerId={auction.seller_id} />
      </div>

      {/* ============ MOBILE STICKY BID BAR (with optional Buy Now) ============ */}
      {!closed && !isSeller && !openEnded && (
        <div
          data-testid="mobile-sticky-bid"
          className="md:hidden fixed bottom-16 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t divider px-4 py-3 soft-shadow"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400">
                {auction.buy_now_price ? "Güncel · Hemen al ↓" : "Güncel teklif"}
              </p>
              <p className="font-mono-tight text-base font-medium text-slate-900 truncate">
                ₺{Number(auction.current_price).toLocaleString("tr-TR")}
              </p>
            </div>
            <button
              data-testid="mobile-bid-btn"
              onClick={placeBid}
              disabled={placing || (auction.buy_now_price && Number(bidAmount) > auction.buy_now_price)}
              className={
                auction.buy_now_price
                  ? "bg-white text-slate-900 border-2 border-slate-900 font-medium px-4 py-2.5 rounded-full disabled:opacity-60 text-sm"
                  : "bg-slate-900 text-white font-medium px-5 py-2.5 rounded-full hover:bg-slate-800 transition-colors disabled:opacity-60 text-sm"
              }
            >
              Teklif ver
            </button>
          </div>
          {auction.buy_now_price && (
            <button
              data-testid="mobile-buy-now-btn-inline"
              onClick={buyNow}
              className="mt-2 w-full bg-slate-900 text-white font-medium px-5 py-3 rounded-full hover:bg-black transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-1.5"><ShoppingBag size={14} weight="fill" /> Hemen al</span>
              <span className="font-mono-tight">₺{Number(auction.buy_now_price).toLocaleString("tr-TR")}</span>
            </button>
          )}
        </div>
      )}
      {/* Buy Now mobile — for open_ended */}
      {openEnded && !closed && !isSeller && auction.buy_now_price && (
        <div className="md:hidden fixed bottom-16 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t divider px-4 py-3 soft-shadow"
             style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <button onClick={buyNow} className="w-full bg-slate-900 text-white font-medium px-5 py-3 rounded-full flex items-center justify-between" data-testid="mobile-buy-now-btn">
            <span className="flex items-center gap-1.5"><ShoppingBag size={14} weight="fill" /> Hemen al</span>
            <span className="font-mono-tight">₺{Number(auction.buy_now_price).toLocaleString("tr-TR")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Badge({ icon: Icon, label, tint = "slate" }) {
  const map = {
    slate: "text-slate-600",
    emerald: "text-emerald-800",
    blue: "text-slate-800",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 ${map[tint]} text-xs font-medium`}>
      <Icon size={12} weight="regular" /> {label}
    </span>
  );
}

function SellerCard({ seller, auction, activeListingsCount = 0 }) {
  const s = seller || {};
  const store = s.store;
  const memberSince = s.created_at ? new Date(s.created_at).getFullYear() : null;
  return (
    <div className="p-6 rounded-2xl bg-white premium-border" data-testid="seller-card">
      <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-4">Satıcı</p>
      <Link
        to={store?.slug ? `/magaza/${store.slug}` : `/kullanici/${auction.seller_id}`}
        className="flex items-center gap-3"
        data-testid={store?.slug ? "seller-store-link" : "seller-link"}
      >
        <div className="relative flex-none">
          {store?.slug ? (
            <div className="w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Storefront size={20} weight="regular" />
            </div>
          ) : s.picture ? (
            <img src={s.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-medium text-xl">
              {(auction.seller_name || "?")[0]}
            </div>
          )}
          {s.is_verified && (
            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-700 text-white rounded-full flex items-center justify-center ring-2 ring-white">
              <ShieldCheck size={10} weight="fill" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-medium text-base text-slate-900 truncate tracking-tight">
            {store?.name || auction.seller_name}
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
            {s.is_verified ? (
              <span className="text-emerald-800 font-medium">Doğrulanmış satıcı</span>
            ) : (
              <span>Doğrulanmamış</span>
            )}
          </div>
        </div>
      </Link>

      {/* Trust stats */}
      <div className="mt-5 pt-5 border-t divider grid grid-cols-3 gap-3" data-testid="seller-stats">
        <div>
          <p className="font-mono-tight text-lg font-medium text-slate-900">{activeListingsCount}</p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-0.5">Aktif ilan</p>
        </div>
        <div>
          <p className="font-mono-tight text-lg font-medium text-slate-900 flex items-center gap-1">
            {s.seller_rating ? s.seller_rating.toFixed(1) : "–"}
            {s.seller_rating && <Star size={12} weight="fill" className="text-amber-500" />}
          </p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-0.5">Puan ({s.review_count || 0})</p>
        </div>
        <div>
          <p className="font-mono-tight text-lg font-medium text-slate-900">{memberSince || "–"}</p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-0.5">Üyelik</p>
        </div>
      </div>

      {(store?.slug || auction.seller_id) && (
        <Link
          to={store?.slug ? `/magaza/${store.slug}` : `/kullanici/${auction.seller_id}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900 pt-3 border-t divider"
          data-testid="seller-profile-link"
        >
          {store?.slug ? "Mağazayı ziyaret et" : "Profili gör"} <CaretRight size={11} weight="bold" />
        </Link>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={13} weight="regular" className="text-slate-600" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 leading-none mb-1">{label}</p>
        <p className="text-sm font-medium text-slate-800 leading-tight truncate">{value}</p>
      </div>
    </div>
  );
}

function EscrowPanel({ auction, isWinner, onConfirm }) {
  const status = auction.escrow_status;
  if (status === "released") {
    return (
      <div className="p-4 rounded-xl bg-emerald-50 text-sm font-medium text-emerald-900 flex items-center gap-2">
        <CheckCircle size={15} weight="fill" /> Ödeme satıcıya aktarıldı
      </div>
    );
  }
  if (status === "held") {
    return (
      <div className="p-5 rounded-xl bg-slate-50 space-y-3">
        <p className="text-sm font-medium text-slate-900 flex items-center gap-2"><ShieldCheck size={15} weight="fill" /> Param güvende</p>
        <p className="text-xs text-slate-500 leading-relaxed">Ödeme havuz hesabında tutuluyor. Alıcı ürünü onayladığında satıcıya aktarılacak.</p>
        {isWinner && (
          <button onClick={onConfirm} data-testid="release-escrow-btn"
            className="w-full bg-emerald-800 text-white font-medium px-4 py-3 rounded-lg hover:bg-emerald-900 transition-colors">
            Ürünü teslim aldım
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="p-4 rounded-xl bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-2">
      <CheckCircle size={15} weight="fill" className="text-emerald-700" /> Ödeme tamamlandı
    </div>
  );
}

function OfferModal({ auctionId, listingTitle, onClose }) {
  const [type, setType] = useState("price");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [tradeId, setTradeId] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/listings").then(({ data }) => setMyListings(data.filter(a => a.status === "active"))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/auctions/${auctionId}/offers`, {
        type,
        amount: type === "price" ? Number(amount) : null,
        trade_listing_id: type === "trade" ? tradeId : null,
        message,
      });
      toast.success("Teklifiniz gönderildi"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 soft-shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="offer-modal">
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Teklif gönder</p>
        <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 mb-6">{listingTitle}</h2>

        <div className="grid grid-cols-2 gap-1 mb-6 bg-slate-50 p-1 rounded-lg">
          <button
            type="button" onClick={() => setType("price")}
            data-testid="offer-type-price"
            className={`px-4 py-2.5 rounded-md font-medium text-sm transition-all ${type === "price" ? "bg-white text-slate-900 soft-shadow" : "text-slate-500 hover:text-slate-900"}`}
          >Fiyat teklifi</button>
          <button
            type="button" onClick={() => setType("trade")}
            data-testid="offer-type-trade"
            className={`px-4 py-2.5 rounded-md font-medium text-sm transition-all ${type === "trade" ? "bg-white text-slate-900 soft-shadow" : "text-slate-500 hover:text-slate-900"}`}
          >Takas teklifi</button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {type === "price" ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 block">Teklif tutarı (₺)</label>
              <input
                type="number" min="1" step="0.01" required
                value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 font-mono-tight focus:outline-none focus:border-slate-900 transition-colors"
                data-testid="offer-amount"
              />
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 block">Takas için önereceğin ilan</label>
              <select
                value={tradeId} onChange={(e) => setTradeId(e.target.value)} required
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:border-slate-900 transition-colors"
                data-testid="offer-trade-select"
              >
                <option value="">Seçin…</option>
                {myListings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
              {myListings.length === 0 && <p className="text-xs text-slate-500 mt-2">Aktif ilanınız yok — takas için önce ilan oluşturmalısınız.</p>}
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 block">Mesaj (opsiyonel)</label>
            <textarea
              rows={3} maxLength={500} value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:border-slate-900 transition-colors"
              data-testid="offer-message"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
            <button
              type="submit" disabled={busy}
              className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-60"
              data-testid="offer-submit"
            >
              {busy ? "Gönderiliyor…" : "Gönder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DopingModal({ auctionId, onClose }) {
  const [packages, setPackages] = useState([]);
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/doping-packages").then(({ data }) => {
      setPackages(data);
      if (data.length > 0) setType(data[0].id);
    });
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/doping-checkout", {
        auction_id: auctionId, doping_type: type, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başlatılamadı"); setBusy(false); }
  };

  const badgeLabel = {
    homepage_vitrin: { label: "Ana Sayfa Vitrin", tone: "bg-amber-50 text-amber-800", icon: "★" },
    kategori_vitrin: { label: "Kategori Vitrin", tone: "bg-emerald-50 text-emerald-800", icon: "✦" },
    top_row: { label: "Üst Sıra", tone: "bg-slate-100 text-slate-700", icon: "▲" },
    bold_title: { label: "Kalın Başlık", tone: "bg-red-50 text-[#B54037]", icon: "B" },
    urgent_badge: { label: "Acil Etiketi", tone: "bg-red-50 text-[#B54037]", icon: "⚡" },
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-8 soft-shadow-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="doping-modal">
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Doping</p>
        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900">İlanını öne çıkar</h2>
        <p className="text-sm text-slate-500 mt-2 mb-6">
          Doping ilanları sıralamada üste taşınır, özel etiketle görünür ve ortalama <span className="font-medium text-slate-900">5 kat daha fazla</span> teklif alır.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="doping-package-grid">
          {packages.map((p) => {
            const badge = badgeLabel[p.id] || { label: p.label, tone: "bg-slate-100 text-slate-700", icon: "★" };
            const selected = type === p.id;
            return (
              <button
                key={p.id} type="button" onClick={() => setType(p.id)}
                data-testid={`doping-${p.id}`}
                className={`text-left p-5 rounded-xl border-2 transition-all ${selected
                  ? "border-slate-900 bg-slate-50 soft-shadow"
                  : "border-slate-100 bg-white hover:border-slate-300"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full ${badge.tone}`}>
                    <span>{badge.icon}</span> {badge.label}
                  </span>
                  {selected && <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-emerald-700">Seçili</span>}
                </div>
                <p className="font-mono-tight text-2xl font-medium text-slate-900 mb-1">₺{p.price}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] font-medium text-slate-400 mb-2">{p.days} gün</p>
                <p className="text-xs text-slate-600 leading-relaxed">{p.description}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-6 p-4 rounded-xl bg-slate-50 text-xs text-slate-500 leading-relaxed">
          <p><strong className="text-slate-700">Nasıl çalışır?</strong> Ödeme başarılı olduğunda ilanınıza doping etiketi eklenir ve sıralamada üste taşınır. Süresi dolan doping otomatik olarak düşer.</p>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
          <button
            onClick={pay} disabled={busy || !type}
            className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-60"
            data-testid="doping-pay"
          >
            {busy ? "Yönlendiriliyor…" : "Ödemeye devam et"}
          </button>
        </div>
      </div>
    </div>
  );
}

const REPORT_REASONS = [
  "Yasaklı ürün",
  "Yanıltıcı bilgi / Sahte ilan",
  "Platform dışı ödeme yönlendirmesi",
  "Uygunsuz içerik",
  "Fiyat manipülasyonu",
  "Diğer",
];

function ReportModal({ auctionId, onClose }) {
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: "listing", target_id: auctionId, reason, message,
      });
      toast.success("Şikayetiniz alındı. Ekibimiz inceleyecek.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Şikayet gönderilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 soft-shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Şikayet</p>
        <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 mb-2">İlanı şikayet et</h2>
        <p className="text-sm text-slate-500 mb-6">Ekibimiz her şikayeti manuel inceler. Yanlış şikayetler hesabınıza etki edebilir.</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 block">Sebep</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="report-reason"
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:border-slate-900 transition-colors">
              {REPORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2 block">Detay (opsiyonel)</label>
            <textarea rows={3} maxLength={500} value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Şikayetinizi kısaca açıklayın..."
              data-testid="report-message"
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:border-slate-900 transition-colors" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
            <button type="submit" disabled={busy}
              className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-60"
              data-testid="report-submit">
              {busy ? "Gönderiliyor…" : "Gönder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



function MessageSellerModal({ auctionId, sellerName, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/messages/start", { listing_id: auctionId, text });
      toast.success("Mesaj gönderildi");
      onClose();
      // Deep link to Messages tab
      nav(`/panelim?tab=messages&conv=${data.conversation.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mesaj gönderilemedi");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 soft-shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="message-seller-modal">
        <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Yeni mesaj</p>
        <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 mb-2">Satıcıya yaz</h2>
        <p className="text-sm text-slate-500 mb-6">
          Telefon numarası veya IBAN paylaşımı yasaktır; risk tespitinde konuşma admin panelinde görünür.
        </p>
        <form onSubmit={submit} className="space-y-5">
          <textarea rows={4} maxLength={1000} value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Merhaba, bu ürün hakkında bilgi alabilir miyim?"
            data-testid="message-seller-text"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:border-slate-900 transition-colors" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
            <button type="submit" disabled={busy || !text.trim()}
              className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-60"
              data-testid="message-seller-submit">
              {busy ? "Gönderiliyor…" : "Gönder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
